import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import api, { getBaseURL } from '../services/api';
import useIsMobile from '../utils/viewport';

// Slot responsivo: sidebar direita no desktop (fixed 300x600), top mobile (320x100)
const DESKTOP_BREAK = 992;
// Snooze de 24h: guarda { [banner_id]: expira_ts_ms }. Ao passar do tempo, volta a aparecer.
const HIDE_KEY = 'ad_banner_snoozed';
const SNOOZE_MS = 24 * 60 * 60 * 1000;

function readSnoozed() {
  try {
    const obj = JSON.parse(localStorage.getItem(HIDE_KEY) || '{}');
    // Limpa expirados na leitura
    const now = Date.now();
    let changed = false;
    for (const k of Object.keys(obj)) {
      if (!obj[k] || obj[k] < now) { delete obj[k]; changed = true; }
    }
    if (changed) localStorage.setItem(HIDE_KEY, JSON.stringify(obj));
    return obj;
  } catch { return {}; }
}
function isHidden(id) {
  const obj = readSnoozed();
  const t = obj[String(id)];
  return t && t > Date.now();
}
function markHidden(id) {
  try {
    const obj = readSnoozed();
    obj[String(id)] = Date.now() + SNOOZE_MS;
    localStorage.setItem(HIDE_KEY, JSON.stringify(obj));
  } catch { /* silencioso */ }
}

// Prefixa /api/... com o backend host quando estamos servindo estatico do frontend
const backendHost = () => {
  const base = getBaseURL(); // ex: https://admin-api.futezap.com.br/api
  return base.replace(/\/api\/?$/, '');
};

// publicMode=true usa endpoint sem auth (pra landings publicas).
// communityMode=true usa endpoint autenticado com community_token (jogador).
// screenOverride: pseudo-path fixo pra rotas dinamicas (ex: '/perfil-publico-time'
// no lugar de '/time/:slug' que muda por time).
export default function AdBanner({ publicMode = false, communityMode = false, screenOverride }) {
  const location = useLocation();
  const [banner, setBanner] = useState(null);
  const isMobile = useIsMobile(DESKTOP_BREAK);
  const [closed, setClosed] = useState(false);
  const screenPath = screenOverride || location.pathname;

  useEffect(() => {
    setClosed(false);
    setBanner(null);
    (async () => {
      try {
        let data;
        if (publicMode) {
          // axios cru pra nao mandar Authorization header (endpoint publico)
          const r = await axios.get(`${getBaseURL()}/banners/public/for-screen`, {
            params: { path: screenPath },
          });
          data = r.data;
        } else if (communityMode) {
          const r = await api.get('/banners/community/for-screen', {
            params: { path: screenPath },
            headers: { Authorization: `Bearer ${localStorage.getItem('community_token')}` },
          });
          data = r.data;
        } else {
          const r = await api.get('/banners/for-screen', { params: { path: screenPath } });
          data = r.data;
        }
        const b = data?.banner;
        if (b && !isHidden(b.id)) setBanner(b);
      } catch { /* silencioso — sem banner nao quebra a tela */ }
    })();
  }, [screenPath, publicMode, communityMode]);

  if (!banner || closed) return null;

  const src = isMobile ? banner.image_mobile_url : banner.image_desktop_url;
  const imgFull = src.startsWith('http') ? src : `${backendHost()}${src}`;
  const clickHref = banner.click_url ? `${backendHost()}${banner.click_url}&screen=${encodeURIComponent(location.pathname)}` : null;

  const closeBtn = (
    <button
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); markHidden(banner.id); setClosed(true); }}
      aria-label="Fechar"
      style={{
        position: 'absolute', top: 4, right: 4, width: 22, height: 22, borderRadius: '50%',
        border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 12,
        cursor: 'pointer', zIndex: 2, lineHeight: 1, display: 'grid', placeItems: 'center',
      }}
    >×</button>
  );

  const img = (
    <img
      src={imgFull}
      alt={banner.title || 'Anúncio'}
      style={{ display: 'block', width: '100%', height: 'auto', borderRadius: isMobile ? 0 : 8 }}
      loading="lazy"
    />
  );

  const inner = clickHref ? (
    <a href={clickHref} target="_blank" rel="noopener sponsored noreferrer">{img}</a>
  ) : img;

  if (isMobile) {
    return (
      <div style={{
        position: 'sticky', top: 64, zIndex: 900, width: '100%',
        maxWidth: 480, margin: '0 auto', padding: '4px 8px',
        background: 'transparent',
      }}>
        <div style={{ position: 'relative', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 6, overflow: 'hidden' }}>
          {closeBtn}
          {inner}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', top: 88, right: 20, width: 300, zIndex: 900,
    }}>
      <div style={{ position: 'relative', boxShadow: '0 6px 20px rgba(0,0,0,0.12)', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        {closeBtn}
        {inner}
        <div style={{ fontSize: 10, color: '#8c8c8c', padding: '4px 8px', background: '#fafafa', textAlign: 'right' }}>
          Anúncio
        </div>
      </div>
    </div>
  );
}
